// Internal imports
import XlinkAPI from '~/app/api/xlinkAPI';
import { toBMPDataURL } from '~/app/bmpConversionHelpers.js';
import { SIGNEE, SIGNEE_TYPE, SIGNATURE_CATEGORY } from '~/app/constants.js';
import { statusOK } from '~/app/webHelpers.js';
import ErrorHelpers from '~/app/errorHelpers.js';
import { pickBy } from 'lodash-es';
// Redux imports
import { store } from '~/app/redux/index';
import { actions as formViewerActions } from '~/app/redux/modules/formViewer';
import { actions as remoteSignActions } from '~/app/redux/remoteSign/duck';

/**
 * @module SignaturesHelper
 * @category Returns
 * @subcategory Signatures
 */

const SIGNATURES = {
  ERO: 'ero_signature',
  PAID_PREPARER: 'preparer_signature',
  AMENDED_PAID_PREPARER: 'preparer_signature',
};

const DEVICE_USED = {
  ERO: 'ero_device_used',
  PAID_PREPARER: 'preparer_device_used',
  AMENDED_PAID_PREPARER: 'preparer_device_used',
};

/**
 * Finds the index of an object in an array.
 *
 * @function
 * @param {Object[]} initialArray The array of objects.
 * @param {string} signeeType The type of signee.
 */
export const findIndex = (initialArray, signeeType) => {
  const index = initialArray.findIndex(item => item.signee === signeeType);

  return index;
};

/**
 * Drops signees whose signature is already on file, so they are not prompted.
 *
 * The preparer is excluded upstream in the reducer for this reason. The amended
 * preparer cannot be excluded there, because the reducer only sees calcserver's
 * signquery — which reports them as missing for every return, since
 * return_print_query carries no signature arguments. They are judged here
 * instead, where the fetched signature data is available, so an amended preparer
 * with nothing on file can still be sent a request.
 *
 * @param {Object} signaturesNeeded Map of signee to whether a signature is needed.
 * @param {Object} amendedPreparerData Fetched amended preparer contact/signature data.
 * @returns {Object} signaturesNeeded without signees that already have a signature.
 */
export const withoutSignaturesOnFile = (signaturesNeeded, amendedPreparerData) => {
  const hasAmendedPreparerSignature = Boolean(
    amendedPreparerData?.signatureData?.[SIGNATURES.AMENDED_PAID_PREPARER],
  );

  if (!hasAmendedPreparerSignature) return signaturesNeeded;

  const remaining = { ...signaturesNeeded };
  delete remaining[SIGNEE.AMENDED_PREPARER];
  return remaining;
};

/**
 * Checks to see which signatures and/or signature requsts are needing to be collected.
 *
 * @function
 * @param {Object} signaturesNeeded The signatures needed on the documents.
 * @param {Object} signaturesCollected The signees with either a signature in the DB or
 * a request for them to sign.
 */
export const remainingSignaturesNeeded = (signaturesNeeded, signaturesCollected) => {
  const diff = Object.keys(pickBy(signaturesNeeded)).reduce((diff, key) => {
    if (signaturesCollected[key] === signaturesNeeded[key]) return diff;
    return {
      ...diff,
      [key]: signaturesNeeded[key],
    };
  }, {});
  return diff;
};

/**
 * Sends a remote signature request via email or text message to the preparer.
 *
 * @param {number} sigDocID The signature document ID from tblTaxReturn_Signature_Documents.
 * @param {number} returnID The return ID of the tax return.
 * @param {Object} signatureRequestData The contact information of the preparer.
 * @param {string} prepRMSEmail Preparer email to copy on the request, when requested.
 * @param {number} preparerID The preparer ID of the return.
 * @param {boolean} isIODSigningMethod Whether the In-Office Device signing method is used.
 * @param {number} signeePreparerID The preparer ID of the signee, which for the amended
 * preparer is not the return's preparer.
 */
const postPreparerRemoteSignatureAuthenticationData = async (
  sigDocID,
  returnID,
  signatureRequestData,
  prepRMSEmail,
  preparerID,
  isIODSigningMethod,
  signeePreparerID,
) => {
  const {
    signeeType,
    contactData: {
      preparer_email: email,
      preparer_cell: cell,
      preparer_carrier: carrier,
      preparer_carrier_domain: carrierDomain,
    },
    contactMethod,
    newEmail,
    cellPhoneCarrier,
  } = signatureRequestData;

  let remoteSignatureOption = '';
  const isInputMethod = contactMethod === 'input';
  const amendedSigneeType = SIGNATURE_CATEGORY.AMENDED_PREPARER;

  remoteSignatureOption = handleRemoteSignatureOption(contactMethod);

  if (isInputMethod) remoteSignatureOption = handleRemoteSignatureOption(newEmail);

  try {
    await XlinkAPI.postRemoteSignatureAuthenticationData(
      sigDocID,
      isInputMethod ? newEmail : email,
      isInputMethod ? newEmail.replace(/[- )(]/g, '') : cell,
      isInputMethod ? cellPhoneCarrier : carrier,
      isInputMethod ? '' : carrierDomain,
      signeeType === SIGNEE_TYPE.AMENDED_PAID_PREPARER_SIGNATURE ? amendedSigneeType : signeeType,
      parseInt(returnID),
      remoteSignatureOption,
      prepRMSEmail,
      preparerID,
      isIODSigningMethod,
      signeePreparerID,
    );
    return true;
  } catch (e) {
    ErrorHelpers.handleError('Signature Request Error', e);
    return false;
  }
};

/**
 * Sends a remote signature request via email or text message to the taxpayer or spouse.
 *
 * @param {number} sigDocID The signature document ID from tblTaxReturn_Signature_Documents.
 * @param {number} returnID The return ID of the tax return.
 * @param {Object} signatureRequestData The contact information of the taxpayer or spouse.
 */
const postRemoteSignatureAuthenticationData = async (
  sigDocID,
  returnID,
  signatureRequestData,
  prepRMSEmail,
  preparerID,
  isIODSigningMethod,
) => {
  const {
    email,
    cellPhone,
    cellCarrier,
    cellDomain,
    signeeType,
    contactMethod,
    newEmail,
    cellPhoneCarrier,
  } = signatureRequestData;
  let remoteSignatureOption = '';
  const isInputMethod = contactMethod === 'input';

  remoteSignatureOption = handleRemoteSignatureOption(contactMethod);

  if (isInputMethod) remoteSignatureOption = handleRemoteSignatureOption(newEmail);

  try {
    await XlinkAPI.postRemoteSignatureAuthenticationData(
      sigDocID,
      isInputMethod ? newEmail : email,
      isInputMethod ? newEmail.replace(/[- )(]/g, '') : cellPhone,
      isInputMethod ? cellPhoneCarrier : cellCarrier,
      isInputMethod ? '' : cellDomain,
      signeeType,
      parseInt(returnID),
      remoteSignatureOption,
      prepRMSEmail,
      preparerID,
      isIODSigningMethod,
    );
    return true;
  } catch (e) {
    ErrorHelpers.handleError('Signature Request Error', e);
    return false;
  }
};

/**
 * Determines the function to be called for signature request.
 *
 * @function
 * @param {Object} signatureRequestData The contact information of the taxpayer or spouse.
 * @param {function} handleSigningRemotely Callback function to handle signing a remote signature.
 * @param {string} documentType The type of document being signed.
 * @param {function} handleSigningInOffice Callback function to handle signing in the office.
 * @param {number} sigDocID used to filter for the specific signature document
 */
export const handleSignatureRequestOption = (
  signatureRequestData,
  handleSigningRemotely,
  documentType,
  handleSigningInOffice,
  sigDocID,
) => {
  const hasRemoteSignatureRequest = signatureRequestData.some(
    el => el.signingMethod === 'Remote Signature',
  );

  const hasInOfficeDeviceRequest = signatureRequestData.some(
    el => el.signingMethod === 'In-Office Device Signature',
  );

  if (hasRemoteSignatureRequest || hasInOfficeDeviceRequest) {
    handleSigningRemotely(documentType);
  } else {
    // If signing a pending remote request signature IN office
    handleSigningInOffice(sigDocID, '', documentType);
  }
};

/**
 * Sends remote signature requests as well as handles if in-office signing is chosen along with the remote signature request.
 *
 * @function
 * @param {number} returnID The return ID of the tax return.
 * @param {string} requestedSignatures The signees of the document.
 * @param {string} pdfBlob The PDF Blob of the current document needing signed.
 * @param {string} printGUID The print GUID of the current tax return.
 * @param {string} messageBlob The message blob of the tax return.
 * @param {object} signatureRequestData The data of the signees and their signing methods.
 * @param {object} eroData The contact and signature data of the ERO.
 * @param {object} preparerData The contact and signature data of the Preparer.
 * @param {object} amendedPreparerData  The contact and signature data of the Amended Preparer.
 * @param {function} handleSigningInOffice Callback function to handle in-office signature.
 * @param {string} bankID The ID of the bank attached to the return.
 * @param {nunber} season The tax season of the request.
 * @param {function} stopSpin Callback function to stop the spinner.
 */
export const sendSignatureRequest = async (
  returnID,
  requestedSignatures,
  pdfBlob,
  printGUID,
  messageBlob,
  signatureRequestData,
  eroData,
  preparerData,
  amendedPreparerData,
  handleSigningInOffice,
  bankID,
  signatureDocID,
  season,
  stopSpin,
  preparerID,
) => {
  try {
    const sendRMSCopyToPrep = store.getState().formViewer.sendRMSCopyToPrep;
    let prepRMSEmail = '';

    const isIODSigningMethod = store.getState().formViewer.isIODSigningMethod;

    if (sendRMSCopyToPrep) {
      prepRMSEmail = store.getState().remoteSign.preparerData.contactData.preparer_email;
    }

    const res = await XlinkAPI.postRemoteSignatureRecord(
      returnID,
      requestedSignatures,
      pdfBlob,
      printGUID,
      messageBlob,
      bankID,
      signatureDocID,
      season,
    );

    if (statusOK(res)) {
      const sigDocID = res.data;
      let isPreparerSignaturePresent = true;
      let isAmendedPreparerSignaturePresent = true;
      const inOfficeSignatures = [];
      const authenticationRecords = [];

      signatureRequestData.forEach(data => {
        // Value only changes once, used in postExistingSignatureData.
        data.signee === SIGNEE.PREPARER && (isPreparerSignaturePresent = false);
        data.signee === SIGNEE.AMENDED_PREPARER && (isAmendedPreparerSignaturePresent = false);

        // Preparers carry their contact details under contactData and use the
        // amended signature category, so they must always go through the preparer
        // helper regardless of the signing method chosen. Routing them through the
        // taxpayer helper sends undefined contact fields and the raw 'A' signee
        // type, which the backend rejects when it converts it to an int.
        const isPreparerSignee = [SIGNEE.PREPARER, SIGNEE.AMENDED_PREPARER].includes(data.signee);

        // The preparer recorded on the request must be the one actually being asked
        // to sign. The amended preparer is chosen on the 1040-X and may not be the
        // the return's preparer, so sending preparerID for them would record the
        // wrong person and the signing portal could not resolve them.
        const signeePreparerID =
          data.signee === SIGNEE.AMENDED_PREPARER
            ? store.getState().formViewer.amendedPreparerID
            : preparerID;

        if (data.signingMethod === 'Remote Signature') {
          // Check if signee type prep
          if (isPreparerSignee) {
            authenticationRecords.push(
              postPreparerRemoteSignatureAuthenticationData(
                sigDocID,
                returnID,
                data,
                // The preparer is the signee here, so there is no separate copy to send
                // them.
                '',
                preparerID,
                isIODSigningMethod,
                signeePreparerID,
              ),
            );
          } else {
            // Spouse / Taxpayer
            authenticationRecords.push(
              postRemoteSignatureAuthenticationData(
                sigDocID,
                returnID,
                data,
                prepRMSEmail,
                preparerID,
                isIODSigningMethod,
              ),
            );
          }
        } else if (data.signingMethod === 'In-Office Device Signature') {
          if (isPreparerSignee) {
            // In-Office Device Signature for the Preparer / Amended Preparer
            authenticationRecords.push(
              postPreparerRemoteSignatureAuthenticationData(
                sigDocID,
                returnID,
                data,
                '',
                preparerID,
                isIODSigningMethod,
                signeePreparerID,
              ),
            );
          } else {
            // In-Office Device Signatures of Spouse / Taxpayer
            data.email = prepRMSEmail;
            authenticationRecords.push(
              postRemoteSignatureAuthenticationData(
                sigDocID,
                returnID,
                data,
                prepRMSEmail,
                preparerID,
                isIODSigningMethod,
              ),
            );
          }
        } else {
          inOfficeSignatures.push(data.signeeType);

          // If we are taxpayer signing in office and we need to collect secondary, add to list.
          if (
            data.signeeType === SIGNEE_TYPE.TAXPAYER &&
            requestedSignatures.includes(SIGNEE_TYPE.TAXPAYER_SECOND_SIGNATURE)
          ) {
            inOfficeSignatures.push(SIGNEE_TYPE.TAXPAYER_SECOND_SIGNATURE);
          }

          // If we are spouse signing in office and we need to collect secondary, add to list.
          if (
            data.signeeType === SIGNEE_TYPE.SPOUSE &&
            requestedSignatures.includes(SIGNEE_TYPE.SPOUSE_SECOND_SIGNATURE)
          ) {
            inOfficeSignatures.push(SIGNEE_TYPE.SPOUSE_SECOND_SIGNATURE);
          }
        }
      });

      const authenticationResults = await Promise.all(authenticationRecords);
      const authenticationFailed = authenticationResults.includes(false);

      await postExistingSignatureData(
        sigDocID,
        eroData,
        SIGNEE_TYPE.ERO,
        SIGNATURES.ERO,
        DEVICE_USED.ERO,
      );
      requestedSignatures.includes(SIGNEE_TYPE.PREPARER) &&
        isPreparerSignaturePresent &&
        (await postExistingSignatureData(
          sigDocID,
          preparerData,
          SIGNEE_TYPE.PREPARER,
          SIGNATURES.PAID_PREPARER,
          DEVICE_USED.PAID_PREPARER,
        ));
      requestedSignatures.includes(SIGNEE_TYPE.AMENDED_PAID_PREPARER_SIGNATURE) &&
        isAmendedPreparerSignaturePresent &&
        (await postExistingSignatureData(
          sigDocID,
          amendedPreparerData,
          SIGNEE_TYPE.AMENDED_PAID_PREPARER_SIGNATURE,
          SIGNATURES.AMENDED_PAID_PREPARER,
          DEVICE_USED.AMENDED_PAID_PREPARER,
        ));

      const error = store.getState().formViewer.errorDialogOpen || authenticationFailed;

      if (inOfficeSignatures.length) {
        if (!error) {
          stopSpin();
          handleSigningInOffice(sigDocID, inOfficeSignatures.join(''), '');
        } else {
          stopSpin();
        }
      } else if (!error) {
        // Do not stop the spinner here. The success dialog's onEnter callback calls
        // stopSpin() as the dialog opens, so the spinner stays up until the modal
        // actually appears. Stopping it now would briefly hand UI control back to the
        // user before the "request has been sent" modal rendered, creating a visible gap.
        store.dispatch(formViewerActions.remoteSignSuccessfulSet(true));
      } else {
        stopSpin();
      }
    }
  } catch (e) {
    stopSpin();
    ErrorHelpers.handleError('Signature Request Error', e);
  } finally {
    store.dispatch(formViewerActions.signatureRequestActiveSet(false));
  }
};

/**
 * Get the electronic signature for the ERO and Preparer as well as their contact information.
 *
 * @function
 * @param {string} efinNumber The EFIN number of the office.
 * @param {string} preparerID The preparer ID associated on the 1040.
 * @param {string} amendedPreparerID The amended preparer ID on the 1040-X.
 * @param {number} returnID The return ID of the tax return.
 */
export const getRemoteData = async (efinNumber, preparerID, amendedPreparerID, returnID) => {
  const getRemoteSignAuthData = await Promise.all([
    efinNumber && XlinkAPI.getefinDataRemoteSignAuth(efinNumber),
    preparerID && XlinkAPI.getppDataRemoteSignAuth(preparerID),
    amendedPreparerID && XlinkAPI.getppDataRemoteSignAuth(amendedPreparerID),
    efinNumber && XlinkAPI.getefinSignature(efinNumber),
    preparerID && XlinkAPI.getppSignature(preparerID, returnID),
    amendedPreparerID && XlinkAPI.getppSignature(amendedPreparerID, returnID),
  ]).catch(() => {
    store.dispatch(formViewerActions.showErrorDialog('Unable to fetch signature data'));
  });

  return getRemoteSignAuthData;
};

/**
 * Fetches the ERO / preparer / amended-preparer remote-signature auth and
 * signature data and dispatches it into Redux. Shared by the Signatures page
 * and the Print-menu remote-signature entrypoint so eroData / preparerData /
 * amendedPreparerData are populated regardless of how the flow is initiated.
 */
export const fetchAndDispatchRemoteData = async (
  efinNumber,
  preparerID,
  amendedPreparerID,
  returnID,
) => {
  const remoteData = await getRemoteData(efinNumber, preparerID, amendedPreparerID, returnID);

  if (!remoteData) {
    // Only clear the fetched auth/signature blobs on a transient fetch
    // failure. Leave signaturesNeeded / initialSigneeData intact so the
    // SendSignature modal flow stays consistent.
    store.dispatch(remoteSignActions.eroDataSet({}));
    store.dispatch(remoteSignActions.preparerDataSet({}));
    store.dispatch(remoteSignActions.amendedPreparerDataSet({}));
    return;
  }

  remoteData[0]?.status === 200 &&
    remoteData[3]?.status === 200 &&
    store.dispatch(
      remoteSignActions.eroDataSet({
        contactData: remoteData[0]?.data,
        signatureData: remoteData[3]?.data,
      }),
    );

  remoteData[1]?.status === 200 &&
    remoteData[4]?.status === 200 &&
    store.dispatch(
      remoteSignActions.preparerDataSet({
        signeeType: SIGNEE_TYPE.PREPARER,
        contactData: remoteData[1]?.data,
        signatureData: remoteData[4]?.data,
      }),
    );

  if (amendedPreparerID)
    remoteData[2]?.status === 200 &&
      remoteData[5]?.status === 200 &&
      store.dispatch(
        remoteSignActions.amendedPreparerDataSet({
          signeeType: SIGNEE_TYPE.AMENDED_PAID_PREPARER_SIGNATURE,
          contactData: remoteData[2]?.data,
          signatureData: remoteData[5]?.data,
        }),
      );
};

/**
 * Handles the signature drawn on the canvas.
 *
 * @function
 * @param {string} data The base64 signature string.
 * @param {object} ctx The context.
 */
const saveCanvasImage = (data, ctx) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(ctx.drawImage(image, 0, 0));
    image.onerror = reject;
    image.src = data;
  });
};

/**
 * Sends the existing Preparer and/or ERO signature to go along with the remote signature request.
 *
 * @function
 * @param {number} sigDocID The Signature Document ID which is the primary key in tblTaxReturn_Signature_Documents.
 * @param {Object} data The signature data of the preparer or ERO.
 * @param {string} signatureType The type of signature whether it is ERO, preparer, or amended preparer.
 * @param {string} signature The type of signature whether it is ERO, preparer, or amended preparer.
 * @param {string} deviceUsed The type of device whether it is ERO, preparer, or amended preparer.
 */
const postExistingSignatureData = async (sigDocID, data, signatureType, signature, deviceUsed) => {
  // Convert signature data fetched from db and convert it to a 2d canvas
  const canvas = document.querySelector('canvas');
  const ctx = canvas.getContext('2d');

  // NOTE - pad type:
  // Topaz and Scriptel devices are no longer supported.
  // We still store `signature_device_id` in the DB to reflect the device used
  // at the time of the original signing.
  //
  // Since on-screen signing is currently the only supported method, we default
  // to `screen` and remove device-specific branching logic (like we did before
  // when checking for each `deviceUsed`).
  //
  // TODO (future):
  // If additional signature devices are reintroduced, we must either:
  // 1) Migrate/update existing `signature_device_id` values in the DB, OR
  // 2) Restore and maintain device-specific logic based on `signature_device_id`
  //    (like we did before for checking each device being used).
  //
  // Example:
  // if (data.signatureData[deviceUsed] === SIGNATURE_PAD_TYPES.ON_SCREEN) {
  //   padType = 'screen';
  // }

  const padType = 'screen';

  const signatureImage = data?.signatureData?.[signature];

  // Create a 2D canvas from signature image
  if (signatureImage) {
    await saveCanvasImage(signatureImage, ctx);
  } else {
    console.log('postExistingSignatureData - Bad Signature Image');
    console.log(
      `postExistingSignatureData - Error Bad Signature Image -> sigDocID: ${sigDocID} - data: ${JSON.stringify(
        data,
      )} - signatureType: ${signature} - deviceUsed: ${deviceUsed}`,
    );

    await XlinkAPI.logErrorMessage(
      'postExistingSignatureData - Bad Signature Image',
      `postExistingSignatureData - Error Bad Signature Image -> sigDocID: ${sigDocID} - data: ${JSON.stringify(
        data,
      )} - signatureType: ${signature} - deviceUsed: ${deviceUsed}`,
      0,
    );
  }

  let sigString = '';

  try {
    // Convert image to a bmp
    sigString = await toBMPDataURL(canvas, padType, deviceUsed, 'postExistingSignatureData');
  } catch (error) {
    console.log('toBMPDataURL - postExistingSignatureData - error: ', error);
    ErrorHelpers.handleError('Error formatting signature 4', error);
  }

  if (!sigString) {
    console.log('postExistingSignatureData - Bad Signature String');
    console.log(
      `postExistingSignatureData - Error Bad Signature String -> sigDocID: ${sigDocID} - data: ${JSON.stringify(
        data,
      )} - signatureType: ${signature} - deviceUsed: ${deviceUsed}`,
    );

    await XlinkAPI.logErrorMessage(
      'postExistingSignatureData - Bad Signature String',
      `postExistingSignatureData - Error Bad Signature String -> sigDocID: ${sigDocID} - data: ${JSON.stringify(
        data,
      )} - signatureType: ${signature} - deviceUsed: ${deviceUsed}`,
      0,
    );
  }

  sigString = sigString.replace('data:image/bmp;base64,', '');

  try {
    await XlinkAPI.postPreparerOrEroExistingRemoteSignature(sigDocID, sigString, signatureType);
  } catch (e) {
    ErrorHelpers.handleError('Signature Request Error', e);
  }

  // Clearing the canvas
  ctx.clearRect(0, 0, canvas.width, canvas.height);
};

/**
 * Takes the contact method and checks if option is email or cell.
 *
 * @function
 * @param {string} val The contact method.
 * @returns {string} email/cell
 */
const handleRemoteSignatureOption = val => {
  if (val?.includes('@')) return 'email';
  else return 'cell';
};

export const getPreparerEmail = () => {
  return store.getState().remoteSign.preparerData.contactData.preparer_email;
};
